import { authHeaders } from '@/lib/authHeaders';

/** The planner stores per-user JSON documents: `config` plus `day:YYYY-MM-DD`.
 *  The shapes live in PlannerView — the API moves them verbatim. */
export async function fetchPlannerDocs(signal?: AbortSignal): Promise<Record<string, unknown>> {
  const res = await fetch('/api/planner', { signal, headers: await authHeaders() });
  if (!res.ok) throw new Error(`Planner ${res.status}`);
  const d = (await res.json()) as { docs: Record<string, unknown> };
  return d.docs || {};
}

export async function savePlannerDocs(docs: Record<string, unknown>): Promise<void> {
  const res = await fetch('/api/planner', {
    method: 'POST',
    headers: { 'content-type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify({ docs }),
  });
  if (!res.ok) throw new Error(`Planner save ${res.status}`);
}

/** The management aggregation — everyone's measured time per client company,
 *  per person and per topic. Super admins only (the server enforces it). */
export interface PlannerTeamUser {
  email: string; name: string;
  totalSec: number; quickN: number;
  tasksDone: number; tasksTotal: number;
  rocksDone: number; rocksPlan: number;
  topCompany: string;
  focusStreak: number;
}
/** A client's monthly budget: hours, and optionally what an hour bills at. */
export interface PlannerTarget { hours: number; rate?: number }
export interface PlannerTeamStats {
  days: number;
  users: PlannerTeamUser[];
  companies: { name: string; sec: number; byUser: { email: string; name: string; sec: number }[] }[];
  topics: { key: string; sec: number }[];
  /** Stopwatches running right now (empty for past windows). */
  live: { email: string; name: string; title: string; company: string; sec: number }[];
  /** Who already closed their 3 rocks today (empty for past windows). */
  today3: string[];
}
export async function fetchPlannerTeam(days: number, end?: string, signal?: AbortSignal): Promise<PlannerTeamStats> {
  const q = new URLSearchParams({ days: String(days) });
  if (end) q.set('end', end); // close the window on a past day (closed-month report)
  const res = await fetch(`/api/planner/team?${q}`, { signal, headers: await authHeaders() });
  if (!res.ok) throw new Error(`Planner team ${res.status}`);
  return res.json();
}

/** Shared monthly budgets per client — one document for the whole team. */
export async function fetchPlannerTargets(signal?: AbortSignal): Promise<Record<string, PlannerTarget>> {
  const res = await fetch('/api/planner/targets', { signal, headers: await authHeaders() });
  if (!res.ok) throw new Error(`Planner targets ${res.status}`);
  const d = (await res.json()) as { targets: Record<string, PlannerTarget> };
  return d.targets || {};
}
export async function savePlannerTargets(targets: Record<string, PlannerTarget>): Promise<Record<string, PlannerTarget>> {
  const res = await fetch('/api/planner/targets', {
    method: 'POST',
    headers: { 'content-type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify({ targets }),
  });
  if (!res.ok) throw new Error(`Planner targets ${res.status}`);
  const d = (await res.json()) as { targets: Record<string, PlannerTarget> };
  return d.targets || {};
}

/** The caller's AI coach note (grounded in their own 14 days of data). */
export async function fetchPlannerCoach(force = false, signal?: AbortSignal): Promise<{ at: string; text: string }> {
  const q = new URLSearchParams(force ? { force: '1' } : {});
  const res = await fetch(`/api/planner/coach?${q}`, { signal, headers: await authHeaders() });
  if (!res.ok) throw new Error(`Planner coach ${res.status}`);
  return res.json();
}

/** Open tickets of the payroll pipelines, importable as tasks. */
export async function fetchPlannerHubspotTickets(signal?: AbortSignal): Promise<{ id: string; subject: string; stage: string }[]> {
  const res = await fetch('/api/planner/hubspot-tickets', { signal, headers: await authHeaders() });
  if (!res.ok) throw new Error(`Planner tickets ${res.status}`);
  const d = (await res.json()) as { tickets: { id: string; subject: string; stage: string }[] };
  return d.tickets || [];
}

/** AI extraction for the import wizard (read-only; the user confirms). */
export interface PlannerImportParse {
  companies: string[];
  templates: { title: string; day: number; type?: string; company?: string; topic?: string }[];
  targets: { company: string; hours: number; rate?: number }[];
}
export async function parsePlannerImport(text: string): Promise<PlannerImportParse> {
  const res = await fetch('/api/planner/import-parse', {
    method: 'POST',
    headers: { 'content-type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify({ text }),
  });
  if (!res.ok) throw new Error(`Planner import ${res.status}`);
  return res.json();
}

/** Real client-company names from HubSpot, for the catalog. */
export async function searchHubspotCompanies(q: string, signal?: AbortSignal): Promise<string[]> {
  const res = await fetch(`/api/planner/hubspot-companies?${new URLSearchParams({ q })}`, { signal, headers: await authHeaders() });
  if (!res.ok) throw new Error(`HubSpot companies ${res.status}`);
  const d = (await res.json()) as { names: string[] };
  return d.names || [];
}
